import { useEffect, useRef } from 'react'

import {
  useOlarkCommands,
  useOlarkEvent,
  useOlarkOperatorCommand,
  useOlarkStore,
  useOlarkSupportOperatorCommands,
  useOlarkSyncVisitorOnBegin,
} from '@fv/client-components'

import { adminUri } from '@/constants'
import { useCurrentUser } from '@/hooks/settings/useAppContext'

const INACTIVITY_TIMEOUT_SECONDS = 300

export const OlarkChat = (): null => {
  const { sendOperatorNotification } = useOlarkCommands()
  const { lastPjChatId } = useOlarkStore()
  const { update } = useOlarkStore(s => s.actions)
  const { accountIds, _id, company, email, firstName, lastName, phoneNumber } =
    useCurrentUser()
  const inactivityTimeoutRef = useRef<number | null>(null)

  const clearInactivityTimer = () => {
    if (inactivityTimeoutRef.current === null) return
    window.clearTimeout(inactivityTimeoutRef.current)
    inactivityTimeoutRef.current = null
  }

  const resetInactivityTimer = () => {
    clearInactivityTimer()
    inactivityTimeoutRef.current = window.setTimeout(() => {
      update({ isConversationActive: false })
    }, INACTIVITY_TIMEOUT_SECONDS * 1000)
  }

  useOlarkSyncVisitorOnBegin({
    userId: _id,
    company,
    email,
    name: `${firstName} ${lastName}`,
    phoneNumber,
    linkLabel: 'Shipper admin',
    viewAsLink: `${adminUri}/accounts/${accountIds[0]}/overview`,
  })

  useOlarkSupportOperatorCommands()

  useOlarkEvent('beginConversation', () => {
    update({ isOlarkBoxOpen: true, isConversationActive: true })
    resetInactivityTimer()
    lastPjChatId &&
      sendOperatorNotification(
        `Last PJ chat: ${adminUri}/tools/chats?chatId=${lastPjChatId}`,
      )
    sendOperatorNotification(
      `Useful commands: \n    !pj: Get a link to the user's latest PJ chat \n   !logrocket: Get a link to the user's Logrocket session`,
    )
  })

  useOlarkEvent('messageToOperator', () => {
    update({ isConversationActive: true })
    resetInactivityTimer()
  })

  useOlarkEvent('messageToVisitor', () => {
    update({ isConversationActive: true })
    resetInactivityTimer()
  })

  useOlarkEvent('boxExpand', () => {
    update({ isOlarkBoxOpen: true })
  })

  useOlarkEvent('boxShrink', () => {
    window.olark?.('api.box.hide', true)
    update({ isOlarkBoxOpen: false })
  })

  useOlarkOperatorCommand('pj', () => {
    sendOperatorNotification(
      lastPjChatId
        ? `Last PJ chat: ${adminUri}/tools/chats?chatId=${lastPjChatId}`
        : 'No PJ chat found',
    )
  })

  useEffect(
    () => () => {
      if (inactivityTimeoutRef.current !== null) {
        window.clearTimeout(inactivityTimeoutRef.current)
      }
      update({ isOlarkBoxOpen: false, isConversationActive: false })
    },
    [update],
  )

  return null
}
